import React from 'react';
import { X } from 'lucide-react';
import { OnboardingFormation } from '../../../services/aiIntelligenceService';

const COMMON_FORMATIONS = [
  '4-4-2', '4-3-3', '4-2-3-1', '3-5-2', '3-4-3',
  '4-1-4-1', '4-5-1', '5-3-2', '5-4-1', '4-4-1-1',
];

const TEAM_SHAPES = [
  { value: 'compact', label: 'Compact', description: 'Short distances between lines' },
  { value: 'stretched', label: 'Stretched', description: 'Width and depth to create space' },
  { value: 'narrow', label: 'Narrow', description: 'Central focus with overlapping fullbacks' },
  { value: 'wide', label: 'Wide', description: 'Use the full width of the pitch' },
  { value: 'flexible', label: 'Flexible', description: 'Shape changes based on game phase' },
];

interface FormationStepProps {
  data: OnboardingFormation;
  onChange: (data: OnboardingFormation) => void;
}

export function FormationStep({ data, onChange }: FormationStepProps) {
  const toggleFormation = (formation: string) => {
    const current = data.preferred || [];
    if (current.includes(formation)) {
      onChange({ ...data, preferred: current.filter((f) => f !== formation) });
    } else {
      onChange({ ...data, preferred: [...current, formation] });
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Formation</h2>
      <p className="text-sm text-gray-600 mb-6">
        Select your preferred formation(s) and team shape. You can choose multiple formations.
      </p>

      {/* Preferred Formations */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Preferred Formation(s) <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {COMMON_FORMATIONS.map((formation) => (
            <button
              key={formation}
              type="button"
              onClick={() => toggleFormation(formation)}
              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all min-h-[44px] ${
                data.preferred?.includes(formation)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {formation}
            </button>
          ))}
        </div>

        {/* Selected formations display */}
        {data.preferred && data.preferred.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {data.preferred.map((f) => (
              <span
                key={f}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
              >
                {f}
                <button
                  type="button"
                  onClick={() => toggleFormation(f)}
                  className="hover:text-blue-900 min-w-[20px] min-h-[20px] flex items-center justify-center"
                  aria-label={`Remove ${f}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Team Shape */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Team Shape <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TEAM_SHAPES.map((shape) => (
            <button
              key={shape.value}
              type="button"
              onClick={() => onChange({ ...data, teamShape: shape.value })}
              className={`text-left p-3 rounded-lg border-2 transition-all min-h-[44px] ${
                data.teamShape === shape.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">{shape.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{shape.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="formation-notes" className="block text-sm font-medium text-gray-700 mb-2">
          Formation Notes <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          id="formation-notes"
          value={data.notes || ''}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Any additional context about your formation preferences..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
      </div>
    </div>
  );
}
